import { z } from 'zod';
import {
  AI_FEATURES,
  aiDraftResultTypeSchema,
  aiDraftStatusSchema,
  aiFeatureSchema,
} from '../enums/ai';
import { taskPrioritySchema } from '../tasks';
import { isoDateTimeSchema, localDateSchema, uuidSchema } from './common';
import { paginated, paginationQuerySchema } from './pagination';
import { PERFORMANCE_LIMITS, monthPeriodSchema } from './performance-common';
import { taskDtoSchema } from './tasks';
import { meetingDecisionDtoSchema, meetingNoteDtoSchema } from './meetings';

/**
 * AI, drafts only (SPEC §8, PLAN §4 "AI (drafts only)", §3.2 "AI: drafts, usage and budget";
 * D-025, D-061, D-108; D-870 ff.). Every AI output is an `ai_drafts` row in the one review queue;
 * AI code never writes tasks, ratings, targets or pay. A human approves a draft through
 * `POST /ai/drafts/:draftId/approve`, which runs the owning domain service under the reviewer's
 * identity, or discards it. All calls go through `AiService`; the monthly cap is reserved
 * atomically before each call (`429 ai_budget_exceeded`, `details.resetsAt`), the whole thing is
 * behind `ai.enabled` (default off, `403 ai_disabled`), and without `ANTHROPIC_API_KEY` every call
 * answers `503 ai_unavailable`. Output that fails its schema twice is `422 ai_output_invalid`.
 * Untrusted text is delimited and treated as data (D-108). The ai workstream owns the routes
 * (`apps/api/src/modules/ai/`).
 */

export const aiDraftIdParamsSchema = z.object({ draftId: uuidSchema });

// ---------------------------------------------------------------- draft payloads (one per draft type)

export const taskDraftPayloadSchema = z.object({
  title: z.string().min(1).max(300),
  description: z.string().max(5_000).default(''),
  /** What the model heard for the owner, e.g. "Mohit from support". */
  ownerHint: z.string().max(200).nullable().default(null),
  /** Validated against the people the reviewer may see; dropped otherwise. */
  suggestedOwnerUserId: uuidSchema.nullable().default(null),
  dueDate: localDateSchema.nullable().default(null),
  dueTime: z
    .string()
    .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/)
    .nullable()
    .default(null),
  dueDatePhrase: z.string().max(100).nullable().default(null),
  priority: taskPrioritySchema.nullable().default(null),
  definitionOfDone: z.string().max(2_000).nullable().default(null),
  kind: z.enum(['task', 'decision_request']).default('task'),
});
export type TaskDraftPayload = z.infer<typeof taskDraftPayloadSchema>;

export const decisionRequestDraftPayloadSchema = taskDraftPayloadSchema.extend({
  kind: z.literal('decision_request').default('decision_request'),
  context: z.string().max(5_000),
  options: z
    .array(
      z.object({
        label: z.string().min(1).max(200),
        details: z.string().max(1_000).nullable().default(null),
      }),
    )
    .min(1)
    .max(8),
  recommendation: z.string().max(2_000).nullable().default(null),
});

export const meetingDecisionDraftPayloadSchema = z.object({
  text: z.string().min(1).max(2_000),
  rationale: z.string().max(2_000).nullable().default(null),
  decidedByHint: z.string().max(200).nullable().default(null),
  /** The transcript lines this came from. */
  segmentSeqs: z.array(z.number().int().nonnegative()).max(50).default([]),
});

export const meetingNotesDraftPayloadSchema = z.object({
  text: z.string().min(1).max(10_000),
  agendaHint: z.string().max(200).nullable().default(null),
  segmentSeqs: z.array(z.number().int().nonnegative()).max(100).default([]),
});

export const threadSummaryPayloadSchema = z.object({
  summary: z.string().min(1).max(5_000),
  keyPoints: z.array(z.string().max(500)).max(12),
  openQuestions: z.array(z.string().max(500)).max(12),
  /** Message ids from the input only (D-108). */
  citedMessageIds: z.array(uuidSchema).max(50),
});
export const catchUpPayloadSchema = z.object({
  summary: z.string().min(1).max(5_000),
  highlights: z.array(z.object({ messageId: uuidSchema, why: z.string().max(500) })).max(15),
});
export const reportSummaryPayloadSchema = z.object({
  narrative: z.string().min(1).max(5_000),
  notableBlockers: z.array(z.object({ userId: uuidSchema, text: z.string().max(500) })).max(20),
});
/** The `summary` draft type covers three features; the feature says which payload it is. */
export const summaryDraftPayloadSchema = z.union([
  threadSummaryPayloadSchema,
  catchUpPayloadSchema,
  reportSummaryPayloadSchema,
]);

export const narrativeDraftPayloadSchema = z.object({ paragraph: z.string().min(1).max(3_000) });

export const appraisalCommentDraftPayloadSchema = z.object({
  /** `{ questionId: text }`: comments only, never a rating (D-025). */
  answers: z.record(z.string(), z.string().max(4_000)),
});

export const aiDraftMetaSchema = z.object({
  model: z.string(),
  promptVersion: z.string(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  /** Cost of the call in paise (company spend, converted at `AI_USD_INR_RATE`). */
  costPaise: z.number().int().nonnegative(),
});
export type AiDraftMeta = z.infer<typeof aiDraftMetaSchema>;

/** What the draft was made from. */
export const aiDraftSourceRefSchema = z.object({
  messageId: uuidSchema.nullable(),
  conversationId: uuidSchema.nullable(),
  transcriptId: uuidSchema.nullable(),
  meetingOccurrenceId: uuidSchema.nullable(),
  weeklyReviewId: uuidSchema.nullable(),
  appraisalId: uuidSchema.nullable(),
  /** `{ segmentSeqs }` or `{ messageIds }`: the lines the draft cites. */
  refs: z.record(z.string(), z.array(z.union([z.string(), z.number()]))).nullable(),
});

const aiDraftBase = z.object({
  id: uuidSchema,
  feature: aiFeatureSchema,
  status: aiDraftStatusSchema,
  sourceRef: aiDraftSourceRefSchema,
  meta: aiDraftMetaSchema,
  confidence: z.string().nullable(),
  /** The order within one extraction. */
  position: z.number().int().nonnegative(),
  requestedByUserId: uuidSchema.nullable(),
  /** The only person who can approve or discard (admins can discard). */
  reviewerUserId: uuidSchema,
  /** The person a narrative or appraisal comment is about. */
  subjectUserId: uuidSchema.nullable(),
  reviewedByUserId: uuidSchema.nullable(),
  reviewedAt: isoDateTimeSchema.nullable(),
  discardReason: z.string().nullable(),
  resultType: aiDraftResultTypeSchema.nullable(),
  resultId: uuidSchema.nullable(),
  expiresAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
  /** For `If-Match`; increments on every change. */
  version: z.number().int().positive(),
});

const variant = <T extends z.ZodType, K extends string>(draftType: K, payload: T) =>
  aiDraftBase.extend({
    draftType: z.literal(draftType),
    payload,
    /** What the reviewer confirmed; null until then. */
    finalPayload: payload.nullable(),
  });

/** `AiDraftDto`: discriminated by `draftType`; `payload` is the model output as validated. */
export const aiDraftDtoSchema = z.discriminatedUnion('draftType', [
  variant('task', taskDraftPayloadSchema),
  variant('decision_request', decisionRequestDraftPayloadSchema),
  variant('meeting_decision', meetingDecisionDraftPayloadSchema),
  variant('meeting_notes', meetingNotesDraftPayloadSchema),
  variant('summary', summaryDraftPayloadSchema),
  variant('narrative', narrativeDraftPayloadSchema),
  variant('appraisal_comment', appraisalCommentDraftPayloadSchema),
]);
export type AiDraftDto = z.infer<typeof aiDraftDtoSchema>;
export const aiDraftResponseSchema = z.object({ draft: aiDraftDtoSchema });

// ---------------------------------------------------------------- the review queue

export const listAiDraftsQuerySchema = paginationQuerySchema.extend({
  status: aiDraftStatusSchema.optional(),
  transcriptId: uuidSchema.optional(),
  feature: aiFeatureSchema.optional(),
});
export const aiDraftListResponseSchema = paginated(aiDraftDtoSchema);

/** `PATCH /ai/drafts/:draftId`: edit a pending draft (`If-Match`; `409 draft_not_pending`). */
export const updateAiDraftRequestSchema = z.strictObject({
  payload: z.record(z.string(), z.unknown()),
});

/** `POST …/approve`: `payload` carries the reviewer's final edits (validated against the draft type). */
export const approveAiDraftRequestSchema = z
  .strictObject({
    payload: z.record(z.string(), z.unknown()).optional(),
  })
  .nullish();
export const approveAiDraftResponseSchema = z.object({
  draft: aiDraftDtoSchema,
  /** What the approval created, through the owning service. */
  result: z.object({ type: aiDraftResultTypeSchema, id: uuidSchema }).nullable(),
  task: taskDtoSchema.optional(),
  decision: meetingDecisionDtoSchema.optional(),
  note: meetingNoteDtoSchema.optional(),
});

export const discardAiDraftRequestSchema = z
  .strictObject({
    reason: z.string().trim().max(PERFORMANCE_LIMITS.reasonMax).optional(),
  })
  .nullish();

export const bulkAiDraftsRequestSchema = z.strictObject({
  draftIds: z.array(uuidSchema).min(1).max(PERFORMANCE_LIMITS.aiBulkMax),
  action: z.enum(['approve', 'discard']),
  /** Per-draft final edits for an approval, keyed by draft id. */
  overrides: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
  reason: z.string().trim().max(PERFORMANCE_LIMITS.reasonMax).optional(),
});
export const bulkAiDraftsResponseSchema = z.object({
  results: z.array(
    z.object({
      draftId: uuidSchema,
      ok: z.boolean(),
      draft: aiDraftDtoSchema.optional(),
      result: z.object({ type: aiDraftResultTypeSchema, id: uuidSchema }).nullable().optional(),
      error: z.object({ code: z.string(), message: z.string() }).optional(),
    }),
  ),
});

// ---------------------------------------------------------------- the features (synchronous)

export const threadSummaryRequestSchema = z.strictObject({ rootMessageId: uuidSchema });
export const catchUpRequestSchema = z.strictObject({
  conversationId: uuidSchema,
  /** Default: the caller's last read position, or the start of today. */
  since: isoDateTimeSchema.optional(),
});
export const reportSummaryRequestSchema = z
  .strictObject({
    date: localDateSchema,
    departmentId: uuidSchema.optional(),
    managerUserId: uuidSchema.optional(),
  })
  .refine((value) => !(value.departmentId && value.managerUserId), {
    message: 'Send departmentId or managerUserId, not both',
    path: ['departmentId'],
  });
export const weeklyNarrativeRequestSchema = z.strictObject({ weeklyReviewId: uuidSchema });
export const appraisalDraftRequestSchema = z.strictObject({
  appraisalId: uuidSchema,
  questionIds: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
});

// ---------------------------------------------------------------- availability and settings

export const AI_UNAVAILABLE_REASONS = [
  'no_api_key',
  'disabled_globally',
  'disabled_in_workspace',
] as const;
export const aiUnavailableReasonSchema = z.enum(AI_UNAVAILABLE_REASONS);

/** `GET /ai/availability`: whether the buttons show, and the plain reason when they do not. */
export const aiAvailabilityResponseSchema = z.object({
  available: z.boolean(),
  /** The workspace switch (`ai.enabled`). */
  enabled: z.boolean(),
  unavailableReason: aiUnavailableReasonSchema.nullable(),
  features: z.array(z.object({ feature: aiFeatureSchema, enabled: z.boolean() })),
  /** The month's budget at a glance. */
  budget: z.object({
    monthlyCapPaise: z.number().int().nonnegative(),
    remainingPaise: z.number().int().nonnegative(),
    exhausted: z.boolean(),
  }),
});

export const aiSettingsSchema = z.object({
  enabled: z.boolean(),
  features: z.object(
    Object.fromEntries(AI_FEATURES.map((feature) => [feature, z.boolean()])) as Record<
      (typeof AI_FEATURES)[number],
      z.ZodBoolean
    >,
  ),
  /** A key is configured on the server and the global switch is on. */
  providerAvailable: z.boolean(),
  unavailableReason: aiUnavailableReasonSchema.nullable(),
});
export type AiSettingsDto = z.infer<typeof aiSettingsSchema>;
export const updateAiSettingsRequestSchema = z.strictObject({
  enabled: z.boolean().optional(),
  features: z
    .strictObject(
      Object.fromEntries(AI_FEATURES.map((feature) => [feature, z.boolean().optional()])) as Record<
        (typeof AI_FEATURES)[number],
        z.ZodOptional<z.ZodBoolean>
      >,
    )
    .optional(),
});

// ---------------------------------------------------------------- usage and budget (admin)

export const aiUsageQuerySchema = z.object({ month: monthPeriodSchema.optional() });
export const aiUsageResponseSchema = z.object({
  month: monthPeriodSchema,
  byFeature: z.array(
    z.object({
      feature: aiFeatureSchema,
      calls: z.number().int().nonnegative(),
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative(),
      costPaise: z.number().int().nonnegative(),
    }),
  ),
  budgetPaise: z.number().int().nonnegative(),
  usedPaise: z.number().int().nonnegative(),
  reservedPaise: z.number().int().nonnegative(),
});

export const aiBudgetResponseSchema = z.object({
  month: monthPeriodSchema,
  monthlyCapPaise: z.number().int().nonnegative(),
  /** Percent of the cap at which admins are notified once (default 80). */
  warnAtPct: z.number().int().min(1).max(100),
  usedPaise: z.number().int().nonnegative(),
  reservedPaise: z.number().int().nonnegative(),
  warnedAt: isoDateTimeSchema.nullable(),
  exhaustedAt: isoDateTimeSchema.nullable(),
  /** The first instant of next month in the workspace timezone: when the counters reset. */
  resetsAt: isoDateTimeSchema,
});
/** `PUT /admin/ai/budget` (Owner): 0 disables AI spend. */
export const putAiBudgetRequestSchema = z.strictObject({
  monthlyCapPaise: z.int().min(0).max(100_000_000_00),
  warnAtPct: z.int().min(1).max(100).optional(),
});
